import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useSession } from "@/features/auth/hooks/useSession";
import { getActiveWorkspaceId } from "@/lib/api/workspace-context";
import { queryKeys } from "@/lib/query/keys";
import { getSynthesis } from "@/lib/api/endpoints/synthesis";
import { useToast } from "@/app/providers/ToastContext";
import type { SynthesisResponse } from "@/features/synthesis/types";
export function useSynthesis() {
  const { token } = useSession();
  const ws = getActiveWorkspaceId();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [regenError, setRegenError] = useState<string | null>(null);

  const q = useQuery({
    queryKey: queryKeys.synthesis.byWorkspace(ws),
    queryFn: () => getSynthesis(token ?? null, ws),
    enabled: Boolean(token && ws),
    staleTime: 30 * 60 * 1000,
  });

  /** Force a server-side recompute (refresh=1), optionally one section. */
  const regenerate = async (section?: string) => {
    if (!token || !ws) return;
    setIsRegenerating(true);
    setRegenError(null);
    try {
      const fresh = await getSynthesis(token, ws, { refresh: true, section });
      // Merge a single-section regen into the cached multi-section response.
      queryClient.setQueryData<SynthesisResponse>(
        queryKeys.synthesis.byWorkspace(ws),
        (prev) => {
          if (!prev || !section) return fresh;
          const byId = new Map(prev.reports.map((r) => [r.section, r]));
          for (const r of fresh.reports) byId.set(r.section, r);
          return { mode: fresh.mode, reports: [...byId.values()] };
        },
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Regeneration failed';
      setRegenError(msg);
      // #4370: surface the failure so Generate is never silent.
      showToast(msg, 'error');
    } finally {
      setIsRegenerating(false);
    }
  };

  return {
    data: q.data,
    isLoading: q.isLoading,
    isError: q.isError,
    isRegenerating,
    regenError,
    refetch: () => q.refetch(),
    regenerate,
  };
}
